// @ts-nocheck
import React, { createContext, useContext, useState, useCallback } from 'react';

// Navigation context for preserving state across view transitions
const NavigationContext = createContext();

export const NavigationProvider = ({ children }) => {
  const [navigationState, setNavigationState] = useState({
    sourceView: null, // 'leads' | 'audiences' | 'campaigns'
    selectedLeads: [],
    filterCriteria: null,
    returnPath: null,
    segmentContext: null,
    preservedState: {}
  });

  // Navigate with context preservation
  const navigateWithContext = useCallback((targetView, context = {}) => {
    setNavigationState(prev => ({
      ...prev,
      ...context,
      sourceView: prev.sourceView || context.sourceView,
      returnPath: context.returnPath || prev.returnPath
    }));
  }, []);

  // Clear navigation context
  const clearNavigationContext = useCallback(() => {
    setNavigationState({
      sourceView: null,
      selectedLeads: [],
      filterCriteria: null,
      returnPath: null,
      segmentContext: null,
      preservedState: {}
    });
  }, []);

  // Update specific context values
  const updateNavigationContext = useCallback((updates) => {
    setNavigationState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Get leads filtered by segment
  const getLeadsBySegment = useCallback((leads, segments, segmentId) => {
    if (!segmentId || segmentId === 'all') return leads;
    
    const segment = segments.find(s => s.id === segmentId);
    if (!segment || !segment.criteria) return [];

    return leads.filter(lead => {
      return segment.criteria.every(criterion => {
        const leadValue = lead[criterion.field];
        switch (criterion.operator) {
          case 'equals':
            return leadValue === criterion.value;
          case 'greater_than':
            return criterion.field === 'leadScore' ? 
              leadValue > criterion.value : 
              new Date(leadValue) > new Date(criterion.value);
          case 'less_than':
            return criterion.field === 'leadScore' ? 
              leadValue < criterion.value : 
              new Date(leadValue) < new Date(criterion.value);
          case 'contains':
            return leadValue && leadValue.toString().toLowerCase().includes(criterion.value.toLowerCase());
          default:
            return false;
        }
      });
    });
  }, []);

  // Get segments that a lead belongs to
  const getSegmentsByLead = useCallback((lead, segments) => {
    return segments.filter(segment => {
      if (!segment.criteria) return false;
      return segment.criteria.every(criterion => {
        const leadValue = lead[criterion.field];
        switch (criterion.operator) {
          case 'equals':
            return leadValue === criterion.value;
          case 'greater_than':
            return criterion.field === 'leadScore' ? 
              leadValue > criterion.value : 
              new Date(leadValue) > new Date(criterion.value);
          case 'less_than':
            return criterion.field === 'leadScore' ? 
              leadValue < criterion.value : 
              new Date(leadValue) < new Date(criterion.value);
          case 'contains':
            return leadValue && leadValue.toString().toLowerCase().includes(criterion.value.toLowerCase());
          default:
            return false;
        }
      });
    });
  }, []);

  const value = {
    navigationState,
    navigateWithContext,
    clearNavigationContext,
    updateNavigationContext,
    getLeadsBySegment,
    getSegmentsByLead
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export default NavigationContext;